<template>
    <div class="payable-receivable-partial-payment-history-container">
        <Loading v-if="loading" />

        <template v-else>
            <div class="payable-receivable-partial-payment-history" v-if="hasHistory">
                <div class="payable-receivable-partial-payment-history-toggle" @click="showHistory = !showHistory">
                    <i v-if="collapsible" class="fa-solid"
                        :class="showHistory ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    <span>Histórico de baixas ({{ history.length }}) </span>
                </div>
                <div class="payable-receivable-partial-payment-history-list" v-if="showHistory || !collapsible">
                    <div class="payable-receivable-partial-payment-history-row" v-for="item in history" :key="item.id"
                        :class="{ 'payable-receivable-partial-payment-history-row--editing': editingHistoryItemId === item.id }">
                        <span class="payable-receivable-partial-payment-history-date">{{ item.conciliationDate }}</span>
                        <span class="payable-receivable-partial-payment-history-value">{{ item.value | currency
                            }}</span>
                        <span class="payable-receivable-partial-payment-history-type">
                            {{ item.typePayment && item.typePayment.content }}
                            <template v-if="item.bankAccount && item.bankAccount.content">
                                - {{ item.bankAccount.content }}
                            </template>
                        </span>
                        <span class="payable-receivable-partial-payment-history-user">
                            <i class="fa-solid fa-user"></i> {{ item.conciliationUser }}
                        </span>
                        <span class="payable-receivable-partial-payment-history-actions">
                            <Badge v-if="editingHistoryItemId === item.id"
                                class="payable-receivable-partial-payment-history-editing-badge" title="Editando"
                                type="warning" size="small" />
                            <Badge v-else-if="isDeletingHistoryItem(item.id)"
                                class="payable-receivable-partial-payment-history-editing-badge" title="Excluindo..."
                                type="danger" size="small" />
                            <template v-else>
                                <i class="fa-solid fa-pen payable-receivable-partial-payment-history-action-icon"
                                    @click="openEditHistoryItem(item)"></i>
                                <i class="fa-solid fa-trash payable-receivable-partial-payment-history-action-icon"
                                    @click="onDeleteHistoryItem(item)"></i>
                            </template>
                        </span>
                    </div>
                </div>
            </div>

            <Alert type="info" v-else>
                <span>Nenhuma baixa parcial registrada.</span>
            </Alert>

            <template v-if="editingHistoryItemId">
                <Alert type="info">
                    <span>Editando a baixa selecionada. Valor possível de editar: {{ valueCap | currency }}.</span>
                </Alert>

                <b-row>
                    <b-col xs="12" sm="12" md="6">
                        <Select title="Conta Bancária" field="bankAccount" :formName="formName" :required="true"
                            placeholder="Selecione a conta bancária" v-model="editForm.bankAccount"
                            url="api/v1/finance/bank-account/select-all" :showNewRegister="false">
                        </Select>
                    </b-col>
                    <b-col xs="12" sm="12" md="6">
                        <Select title="Tipo de Pagamento" field="typePayment" :formName="formName" :required="true"
                            placeholder="Selecione o tipo de pagamento" v-model="editForm.typePayment"
                            url="/api/v1/finance/type-payment/select-all" nameNewRegister="typePaymentCreateUpdate"
                            titleNewRegister="Tipo de Pagamento" :widthNewRegister="500" :heightNewRegister="250">
                            <TypePaymentCreateUpdate :registerInSelect="true" v-model="editForm.typePayment" />
                        </Select>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col xs="12" sm="12" md="6">
                        <InputDecimal title="Valor da Baixa" field="value" :formName="formName" :required="true"
                            v-model="editForm.value" />
                        <span class="payable-receivable-partial-payment-history-error" v-if="exceededMessage">{{
                            exceededMessage }}</span>
                    </b-col>
                    <b-col xs="12" sm="12" md="6">
                        <DateTime v-model="editForm.conciliationDate" :title="'Data de Conciliação'"
                            field="conciliationDate" :formName="formName" :format="'DD/MM/YYYY'"
                            :placeholder="'Selecione a data de conciliação'" />
                    </b-col>
                </b-row>

                <b-row>
                    <b-col sm="12">
                        <div class="payable-receivable-partial-payment-history-edit-actions">
                            <Button _key="btnCancelHistoryEdit" type="danger" title="Cancelar edição" size="small"
                                :clicked="cancelEditHistoryItem" />
                            <Button _key="btnUpdateHistoryItem" type="primary" title="Atualizar" size="small"
                                :loading="isLoading('btnUpdateHistoryItem')" :disabled="!canUpdateHistoryItem"
                                :clicked="updateHistoryItem" />
                        </div>
                    </b-col>
                </b-row>
            </template>
        </template>
    </div>
</template>

<script>
import Alert from "@/ui/layout/Alert";
import Badge from "@/ui/layout/Badge";
import Button from "@/ui/forms/Button";
import DateTime from "@/ui/forms/DateTime";
import InputDecimal from "@/ui/forms/InputDecimal";
import Loading from "@/ui/shared/Loading.vue";
import Select from "@/ui/forms/Select";
import TypePaymentCreateUpdate from "@/modules/finance/components/typePayment/TypePaymentCreateUpdate.vue";

import PayableReceivableService from "../../services/payableReceivable.service.js";

import { mapGetters, mapMutations } from "vuex";

export default {
    name: "PayableReceivablePartialPaymentHistory",
    props: {
        payableReceivableId: { type: String, required: true },
        collapsible: { type: Boolean, default: true },
    },
    components: {
        Alert,
        Badge,
        Button,
        DateTime,
        InputDecimal,
        Loading,
        Select,
        TypePaymentCreateUpdate,
    },
    data() {
        return {
            loading: false,
            history: [],
            saldo: 0,
            showHistory: false,
            editingHistoryItemId: "",
            editingHistoryItemOriginalValue: 0,
            editForm: {
                value: 0,
                conciliationDate: "",
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            },
            exceededMessage: "",
        };
    },
    mounted() {
        this.loadHistory();
    },
    computed: {
        ...mapGetters("generic", ["isLoading"]),
        formName() {
            return `partialPaymentHistory-${this.payableReceivableId}`;
        },
        hasHistory() {
            return this.history.length > 0;
        },
        valueCap() {
            return this.saldo + this.editingHistoryItemOriginalValue;
        },
        canUpdateHistoryItem() {
            return !!this.editForm.bankAccount?.id
                && !!this.editForm.typePayment?.id
                && this.editForm.value > 0
                && this.editForm.value <= this.valueCap;
        },
    },
    methods: {
        ...mapMutations("generic", ["addLoading", "removeLoading", "addEvent"]),
        ...mapMutations("validation", ["addValidation", "removeValidation"]),
        formatCurrency(value) {
            return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        },
        async loadHistory() {
            if (!this.payableReceivableId) return;

            this.loading = true;
            const response = await PayableReceivableService.getAllPartialPayment(this.payableReceivableId);
            if (response.success) {
                this.history = response.content.items || [];
                this.saldo = response.content.remainingValue ?? 0;
                this.$emit("loaded", response.content);
            }
            this.loading = false;
        },
        openEditHistoryItem(item) {
            this.editingHistoryItemId = item.id;
            this.editingHistoryItemOriginalValue = Number(item.value) || 0;
            this.editForm = {
                value: item.value,
                conciliationDate: item.conciliationDate,
                typePayment: item.typePayment?.id ? { ...item.typePayment } : { id: "", content: "" },
                bankAccount: item.bankAccount?.id ? { ...item.bankAccount } : { id: "", content: "" },
            };
        },
        cancelEditHistoryItem() {
            this.removeLoading(["btnCancelHistoryEdit"]);
            this.editingHistoryItemId = "";
            this.editingHistoryItemOriginalValue = 0;
            this.exceededMessage = "";
            this.editForm = {
                value: 0,
                conciliationDate: "",
                typePayment: { id: "", content: "" },
                bankAccount: { id: "", content: "" },
            };
        },
        async updateHistoryItem() {
            this.addLoading("btnUpdateHistoryItem");
            const model = {
                id: this.editingHistoryItemId,
                value: this.editForm.value,
                conciliationDate: this.editForm.conciliationDate,
                typePayment: this.editForm.typePayment,
                bankAccount: this.editForm.bankAccount,
            };
            const response = await PayableReceivableService.updatePartialPayment(model);
            this.removeLoading(["btnUpdateHistoryItem"]);
            if (response.success) {
                this.cancelEditHistoryItem();
                await this.loadHistory();
                this.addEvent({ name: "actionsUpdated" });
                this.$emit("updated");
            }
        },
        deleteHistoryItemLoadingKey(id) {
            return `btnDeleteHistoryItem-${id}`;
        },
        isDeletingHistoryItem(id) {
            return this.isLoading(this.deleteHistoryItemLoadingKey(id));
        },
        async onDeleteHistoryItem(item) {
            if (this.isDeletingHistoryItem(item.id)) return;

            this.addLoading(this.deleteHistoryItemLoadingKey(item.id));
            const response = await PayableReceivableService.deletePartialPaymentById(item.id);
            this.removeLoading([this.deleteHistoryItemLoadingKey(item.id)]);
            if (response.success) {
                await this.loadHistory();
                this.addEvent({ name: "actionsUpdated" });
                this.$emit("updated");
            }
        },
        validateValue(rawValue) {
            const key = "value&" + this.formName;
            this.removeValidation({ key, formName: this.formName });
            this.exceededMessage = "";

            if (rawValue > this.valueCap) {
                this.exceededMessage = `O valor da baixa não pode ser maior que o saldo disponível (${this.formatCurrency(this.valueCap)}).`;
                this.addValidation({
                    key,
                    formName: this.formName,
                    notification: this.exceededMessage,
                });
            }
        },
    },
    watch: {
        payableReceivableId(newId, oldId) {
            if (newId && newId !== oldId) {
                this.cancelEditHistoryItem();
                this.loadHistory();
            }
        },
        editingHistoryItemId(value) {
            this.$emit("hide-actions", !!value);
        },
        "editForm.value"(newVal) {
            this.validateValue(newVal);
        },
    },
};
</script>

<style scoped>
.payable-receivable-partial-payment-history-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
}

.payable-receivable-partial-payment-history {
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;
}

.payable-receivable-partial-payment-history-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    background: var(--bg-elevated);
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 500;
}

.payable-receivable-partial-payment-history-list {
    display: flex;
    flex-direction: column;
    max-height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
}

.payable-receivable-partial-payment-history-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid var(--border-subtle);
    font-size: 13px;
    color: var(--text-primary);
}

.payable-receivable-partial-payment-history-row--editing {
    background: var(--bg-elevated);
    border-left: 3px solid var(--color-brand);
    padding-left: 9px;
}

.payable-receivable-partial-payment-history-editing-badge {
    margin: 0;
    display: inline-flex;
    align-items: center;
}

.payable-receivable-partial-payment-history-editing-badge :deep(.side-by-side) {
    display: inline-flex;
}

.payable-receivable-partial-payment-history-editing-badge :deep(.badge) {
    margin-left: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.payable-receivable-partial-payment-history-edit-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.payable-receivable-partial-payment-history-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
}

.payable-receivable-partial-payment-history-action-icon {
    color: var(--text-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: color 0.15s;
}

.payable-receivable-partial-payment-history-action-icon:hover {
    color: var(--color-brand);
}

.payable-receivable-partial-payment-history-value {
    color: var(--color-success);
    font-weight: 600;
    text-align: right;
}

.payable-receivable-partial-payment-history-type {
    color: var(--text-secondary);
    font-size: 12px;
}

.payable-receivable-partial-payment-history-user {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    justify-self: end;
    color: var(--text-secondary);
    font-size: 12px;
}

.payable-receivable-partial-payment-history-error {
    display: block;
    color: var(--color-danger);
    font-size: 13px;
    margin-top: -4px;
}
</style>
